import React from "react";
import {
  DASHBOARD_MEMBER_FILTERS,
  isDashboardSortAllowedForMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";

/**
 * Shared sort + member-category filter tabs for dashboard detail pages.
 * Matches Total Family Members: sorts | Start/Top, Descendants, …, In Relationship.
 *
 * @param {object} props
 * @param {string} props.sortBy
 * @param {(next: string) => void} props.onSortChange
 * @param {string} props.memberFilter
 * @param {(filter: string) => void} props.onMemberFilterToggle
 * @param {boolean} [props.excludeDeceasedFilters] — hide Descendants/In-Laws (Deceased) on deceased reports
 * @param {string[]} [props.excludedMemberFilters] — member filters hidden on a specific page
 * @param {Object<string, string[]>} [props.pageDisabledSortsByMemberFilter] — sorts a page
 *   puts out of reach for a member tab, on top of the shared rules
 * @param {string} [props.defaultSort] — the order a page already opens in; while that
 *   sort is the active one it is grayed out, since re-picking it changes nothing
 * @param {Object<string, string>} [props.defaultSortByMemberFilter] — for pages that open
 *   with no sort picked (sortBy "none"): the order each member tab already reads in, so
 *   that sort is grayed out until another one takes over
 * @param {boolean} [props.showMemberFilters=true]
 * @param {boolean} [props.showLivingDeceasedFilters=false] — Living / Deceased status tabs
 * @param {string} [props.defaultMemberFilter] — for a page that opens on a member tab
 *   rather than on everyone: that tab is grayed out while it is the one on screen,
 *   as the default sort is, and lights back up once another tab takes over
 * @param {boolean} [props.showAgeSorts=true] — Oldest/Youngest
 * @param {boolean} [props.showOldestSort=true] — Oldest to Youngest on its own,
 *   for a page that offers Youngest to Oldest but not its opposite
 * @param {boolean} [props.showColorSort=true]
 * @param {React.ReactNode} [props.leadingSortButtons] — e.g. Date for birthdays
 * @param {React.ReactNode} [props.trailingFilterButtons] — a page's own filters,
 *   after the divider (e.g. Spouse(s) (Widowed/Deceased) on Wedding Anniversaries)
 * @param {string} [props.inLawsFilterKey] — filter key used by the In-Laws tab (e.g. deceased in-laws on memorials)
 * @param {string} [props.inLawsLabel] — label for the In-Laws tab
 * @param {string} [props.descendantsDeceasedLabel] — label for the Descendants (Deceased) tab
 * @param {boolean} [props.relatedInsteadOfDescendants=false] — show Related / Related
 *   (Living) / Related (Deceased) in place of the three Descendants tabs; those
 *   count the Start/Top of Line couple in with the blood family
 * @param {string} [props.className]
 * @param {string} [props.buttonClassName]
 */
function DashboardDetailSortFilterBar({
  sortBy,
  onSortChange,
  memberFilter = DASHBOARD_MEMBER_FILTERS.ALL,
  onMemberFilterToggle,
  excludeDeceasedFilters = false,
  excludedMemberFilters = [],
  pageDisabledSortsByMemberFilter = {},
  defaultSort = null,
  defaultSortByMemberFilter = {},
  showMemberFilters = true,
  showLivingDeceasedFilters = false,
  defaultMemberFilter = null,
  showAgeSorts = true,
  showOldestSort = true,
  showColorSort = true,
  leadingSortButtons = null,
  trailingFilterButtons = null,
  inLawsFilterKey = DASHBOARD_MEMBER_FILTERS.IN_LAWS,
  inLawsLabel = "In-Laws (Living)",
  descendantsDeceasedLabel = "Descendants (Deceased)",
  relatedInsteadOfDescendants = false,
  className = "dashboard-detail-sort-toggle",
  buttonClassName = "dashboard-detail-sort-btn",
}) {
  const isMemberFilterExcluded = (filter) =>
    excludedMemberFilters.includes(filter);

  // The three blood-family tabs: Descendants leave the Start/Top of Line couple
  // to their own tab; Related counts them in. Same slots on the bar either way.
  const familyTabs = relatedInsteadOfDescendants
    ? {
        all: {
          key: DASHBOARD_MEMBER_FILTERS.RELATED,
          label: "Related",
          title: "Show only related members (non in-laws), including Start/Top of Line",
        },
        living: {
          key: DASHBOARD_MEMBER_FILTERS.RELATED_LIVING,
          label: "Related (Living)",
          title: "Show only living related members, including Start/Top of Line",
        },
        deceased: {
          key: DASHBOARD_MEMBER_FILTERS.RELATED_DECEASED,
          label: "Related (Deceased)",
          title: "Show only deceased related members, including Start/Top of Line",
        },
      }
    : {
        all: {
          key: DASHBOARD_MEMBER_FILTERS.DESCENDANTS,
          label: "Descendants",
          title: "Show only descendants (non in-laws)",
        },
        living: {
          key: DASHBOARD_MEMBER_FILTERS.DESCENDANTS_LIVING,
          label: "Descendants (Living)",
          title: "Show only living descendants",
        },
        deceased: {
          key: DASHBOARD_MEMBER_FILTERS.DESCENDANTS_DECEASED,
          label: descendantsDeceasedLabel,
          title: "Show only deceased descendants",
        },
      };

  // A sort the active member tab cannot be paired with stays on screen but is
  // taken out of reach, so the bar shows what this tab does and does not offer
  // instead of swallowing the click. Switching tabs re-enables whatever the new
  // tab allows.
  // A page can withhold more than the shared rules do — e.g. Birthdays takes the
  // age sorts out of reach on its Deceased tab.
  // The order the page opens in is grayed out while it is the one on screen:
  // picking it again would change nothing. It lights back up as a normal choice
  // the moment another sort takes over, so the default view stays reachable.
  // Pages that open with nothing picked say the same thing per member tab: with
  // sortBy "none" the list is already read in that tab's own order.
  const unsortedDefaultSort = defaultSortByMemberFilter[memberFilter] ?? null;

  const isDefaultSortInEffect = (value) =>
    (defaultSort !== null && value === defaultSort && sortBy === defaultSort) ||
    (sortBy === "none" &&
      unsortedDefaultSort !== null &&
      value === unsortedDefaultSort);

  const isSortDisabled = (value) =>
    isDefaultSortInEffect(value) ||
    !isDashboardSortAllowedForMemberFilter(value, memberFilter) ||
    (pageDisabledSortsByMemberFilter[memberFilter] || []).includes(value);

  const sortButtonClassName = (value) =>
    `${buttonClassName}${sortBy === value ? " active" : ""}${
      isSortDisabled(value) ? " dashboard-detail-sort-btn--disabled" : ""
    }`;

  const toggleSort = (value) => {
    if (isSortDisabled(value)) return;
    onSortChange(sortBy === value ? "none" : value);
  };

  // Every sort the active tab withholds is grayed out the same way, whether it
  // is withheld because the tab already lands in that order or because the sort
  // does not apply to that list.
  const sortButtonProps = (value, title) => ({
    type: "button",
    onClick: () => toggleSort(value),
    className: sortButtonClassName(value),
    "data-sort": value,
    disabled: isSortDisabled(value),
    "aria-disabled": isSortDisabled(value),
    title: isDefaultSortInEffect(value)
      ? "Already the default view"
      : isSortDisabled(value)
      ? "Not available with the selected member filter"
      : title,
  });

  return (
    <div className={className}>
      {leadingSortButtons}
      <button {...sortButtonProps("alpha-asc", "Sort A-Z")}>A - Z</button>
      <button {...sortButtonProps("alpha-desc", "Sort Z-A")}>Z - A</button>
      {showColorSort && (
        <button {...sortButtonProps("color", "Sort by Line Color")}>
          Line Color
        </button>
      )}
      {showAgeSorts && (
        <>
          {showOldestSort && (
            <button {...sortButtonProps("oldest", "Sort Oldest to Youngest")}>
              Oldest to Youngest
            </button>
          )}
          <button {...sortButtonProps("youngest", "Sort Youngest to Oldest")}>
            Youngest to Oldest
          </button>
        </>
      )}

      {showMemberFilters && typeof onMemberFilterToggle === "function" && (
        <>
          <span className="dashboard-detail-sort-divider" aria-hidden="true" />
          {!isMemberFilterExcluded(DASHBOARD_MEMBER_FILTERS.START_TOP) && (
            <button
              type="button"
              onClick={() => onMemberFilterToggle(DASHBOARD_MEMBER_FILTERS.START_TOP)}
              className={`${buttonClassName} ${
                memberFilter === DASHBOARD_MEMBER_FILTERS.START_TOP ? "active" : ""
              }`}
              title="Show only Start/Top of Line"
            >
              Start/Top of Line
            </button>
          )}
          {!isMemberFilterExcluded(familyTabs.all.key) && (
            <button
              type="button"
              onClick={() => onMemberFilterToggle(familyTabs.all.key)}
              className={`${buttonClassName} ${
                memberFilter === familyTabs.all.key ? "active" : ""
              }`}
              title={familyTabs.all.title}
            >
              {familyTabs.all.label}
            </button>
          )}
          {!isMemberFilterExcluded(familyTabs.living.key) && (
            <button
              type="button"
              onClick={() => onMemberFilterToggle(familyTabs.living.key)}
              className={`${buttonClassName} ${
                memberFilter === familyTabs.living.key ? "active" : ""
              }`}
              title={familyTabs.living.title}
            >
              {familyTabs.living.label}
            </button>
          )}
          {!excludeDeceasedFilters &&
            !isMemberFilterExcluded(familyTabs.deceased.key) && (
            <button
              type="button"
              onClick={() => onMemberFilterToggle(familyTabs.deceased.key)}
              className={`${buttonClassName} ${
                memberFilter === familyTabs.deceased.key ? "active" : ""
              }`}
              title={familyTabs.deceased.title}
            >
              {familyTabs.deceased.label}
            </button>
          )}
          {!isMemberFilterExcluded(inLawsFilterKey) && (
            <button
              type="button"
              onClick={() => onMemberFilterToggle(inLawsFilterKey)}
              className={`${buttonClassName} ${
                memberFilter === inLawsFilterKey ? "active" : ""
              }`}
              title={`Show only ${inLawsLabel.toLowerCase()}`}
            >
              {inLawsLabel}
            </button>
          )}
          {!excludeDeceasedFilters &&
            !isMemberFilterExcluded(DASHBOARD_MEMBER_FILTERS.IN_LAWS_DECEASED) && (
            <button
              type="button"
              onClick={() =>
                onMemberFilterToggle(DASHBOARD_MEMBER_FILTERS.IN_LAWS_DECEASED)
              }
              className={`${buttonClassName} ${
                memberFilter === DASHBOARD_MEMBER_FILTERS.IN_LAWS_DECEASED
                  ? "active"
                  : ""
              }`}
              title="Show only deceased in-laws"
            >
              In-Laws (Deceased)
            </button>
          )}
          {!isMemberFilterExcluded(DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP) && (
            <button
              type="button"
              onClick={() =>
                onMemberFilterToggle(DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP)
              }
              className={`${buttonClassName} ${
                memberFilter === DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP
                  ? "active"
                  : ""
              }`}
              title="Show only in-laws in a relationship (not married)"
            >
              In Relationship
            </button>
          )}
        </>
      )}

      {trailingFilterButtons && (
        <>
          <span className="dashboard-detail-sort-divider" aria-hidden="true" />
          {trailingFilterButtons}
        </>
      )}

      {showLivingDeceasedFilters && typeof onMemberFilterToggle === "function" && (
        <>
          {[
            { key: DASHBOARD_MEMBER_FILTERS.LIVING, label: "Living", title: "Show only living members" },
            { key: DASHBOARD_MEMBER_FILTERS.DECEASED, label: "Deceased", title: "Show only deceased members" },
          ].map(({ key, label, title }) => {
            // The tab a page opens on is out of reach while it is on screen:
            // picking it again would change nothing.
            const isDefaultInEffect =
              defaultMemberFilter !== null && key === defaultMemberFilter && memberFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  if (!isDefaultInEffect) onMemberFilterToggle(key);
                }}
                className={`${buttonClassName} ${memberFilter === key ? "active" : ""}${
                  isDefaultInEffect ? " dashboard-detail-sort-btn--disabled" : ""
                }`}
                disabled={isDefaultInEffect}
                aria-disabled={isDefaultInEffect}
                title={isDefaultInEffect ? "Already the default view" : title}
              >
                {label}
              </button>
            );
          })}
        </>
      )}
    </div>
  );
}

export default DashboardDetailSortFilterBar;
